import type { CSSProperties, ReactNode } from 'react';
import { StratosSurfaceProvider } from '@voldzi/stratos-ui/core';
import { useI18n } from '../i18n/i18n-provider';

// Event owns its branding; shared primitives inherit these tokens, including portals.
const eventTokens = {
  '--stratos-color-primary': 'var(--brand-accent)',
  '--stratos-color-primary-soft': 'var(--brand-accent-soft)',
  '--stratos-color-on-primary': 'var(--color-accent-contrast)',
  '--stratos-color-accent': 'var(--brand-accent)',
  '--stratos-color-accent-strong': 'var(--brand-accent-strong)',
  '--stratos-color-accent-soft': 'var(--brand-accent-soft)',
  '--stratos-color-text': 'var(--color-text)',
  '--stratos-color-muted': 'var(--color-text-muted)',
  '--stratos-color-text-secondary': 'var(--color-text-muted)',
  '--stratos-color-border-strong': 'var(--color-border-strong)',
  '--stratos-color-surface': 'var(--color-surface-strong)',
  '--stratos-color-focus-ring': 'var(--brand-accent)',
  '--stratos-font-sans': "'Inter', 'Avenir Next', 'Segoe UI', sans-serif",
  '--stratos-font-size-base': '16px',
} as CSSProperties;

/** Resolve brand colors on the local surface before a portal leaves its DOM subtree. */
function localSurfaceTokens(style?: CSSProperties): CSSProperties {
  const tokens = style as (CSSProperties & Record<string, unknown>) | undefined;
  const mappings = {
    '--stratos-color-primary': '--brand-accent',
    '--stratos-color-primary-soft': '--brand-accent-soft',
    '--stratos-color-on-primary': '--color-accent-contrast',
    '--stratos-color-accent': '--brand-accent',
    '--stratos-color-accent-strong': '--brand-accent-strong',
    '--stratos-color-accent-soft': '--brand-accent-soft',
    '--stratos-color-focus-ring': '--brand-accent',
    '--stratos-font-sans': '--workspace-font-sans',
    '--stratos-color-text': '--color-text',
    '--stratos-color-muted': '--color-text-muted',
    '--stratos-color-text-secondary': '--color-text-muted',
    '--stratos-color-surface': '--color-surface-strong',
    '--stratos-color-border-strong': '--color-border-strong',
  } as const;
  return Object.fromEntries(Object.entries(mappings).flatMap(([surfaceToken, brandToken]) => {
    const value = tokens?.[brandToken];
    return tokens?.[surfaceToken] === undefined && typeof value === 'string'
      ? [[surfaceToken, value]]
      : [];
  })) as CSSProperties;
}

export function EventUiProvider({ children, style, tenantScope = false }: {
  readonly children: ReactNode;
  readonly style?: CSSProperties;
  readonly tenantScope?: boolean;
}) {
  const { locale } = useI18n();
  return <StratosSurfaceProvider locale={locale} theme="light" density="comfortable"
    className={`event-ui${tenantScope ? ' event-ui--tenant' : ''}`} style={{ ...eventTokens, ...localSurfaceTokens(style), ...style }}>
    {children}
  </StratosSurfaceProvider>;
}
